/**
 * Admin-only eye-tracking lab (/admin/eye-tracking).
 *
 * Live gaze sandbox, fully in-browser — NOT wired into studies. MediaPipe Face
 * Landmarker (iris landmarks, WASM) feeds a per-session ridge-regression
 * calibration, then a blank screen renders the live gaze estimate as a
 * one-euro-smoothed dot with a fading trail.
 *
 * The calibration follows the study variants (lib/calibrationVariants: 1/3/5
 * head poses) and, like Miłosz's sandbox (gaze/capture/pose.py), measures the
 * head pose instead of trusting the user: every pose starts with a guidance
 * screen that says which way to turn and holds until the head sits inside the
 * target, and a point stops collecting the moment the head drifts out of it.
 *
 * Two engines, switchable on the setup screen:
 *  - "cnn" (default) — Miłosz's engine as the ECS gaze sidecar runs it: the
 *    v0.1.0 model.onnx (same file, same sha) on a virtual-camera crop, then the
 *    container's fixed ridge spec (lib/gaze/cnnExtractor + cnnCalibrator).
 *    The only difference from the container is who decodes the frames.
 *  - "landmarks" — the lab's older hand-made model on MediaPipe iris/head
 *    features, kept for side-by-side comparison. It is NOT Miłosz's engine.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import GazePropsLayer from '@/components/admin/GazePropsLayer'
import { GazeTracker, type GazeFrame } from '@/lib/gaze/gazeTracker'
import { GazeModel, type FitStats } from '@/lib/gaze/gazeModel'
import { CnnCalibrator } from '@/lib/gaze/cnnCalibrator'
import { CnnExtractor } from '@/lib/gaze/cnnExtractor'
import { loadLabModel } from '@/lib/gaze/labModel'
import { OneEuroFilter2D } from '@/lib/gaze/oneEuro'
import {
  CALIBRATION_VARIANTS,
  CALIBRATION_VARIANT_SHAPES,
  DEFAULT_CALIBRATION_VARIANT,
  type CalibrationTarget,
  type HeadPose,
} from '@/lib/calibrationVariants'
import type { CalibrationVariant } from '@/services/projectTypes'
import { enterFullscreen, exitFullscreenAndUnlock } from '@/utils/fullscreen'
import PoseGuide from '@/components/gaze/PoseGuide'
import {
  BASELINE_MAX_PITCH,
  BASELINE_MAX_YAW,
  EMPTY_POSE_VIEW,
  POSE_ENTER_DEG,
  POSE_HOLD_MS,
  POSE_TOLERANCE_DEG,
  clamp01,
  headFromBase,
  poseOffset,
  within,
  type HeadDeg,
  type PoseView,
} from '@/lib/gaze/headPose'

type Phase = 'setup' | 'calibrating' | 'validating' | 'summary' | 'tracking'

const VARIANT_LABEL: Record<CalibrationVariant, string> = {
  'quick-4x1': 'Krótka · 4 pkt × 1 poza',
  'standard-9x3': 'Średnia · 9 pkt × 3 pozy',
  'full-9x5': 'Długa · 9 pkt × 5 póz',
}

const POSE_COPY: Record<HeadPose, { title: string; hint: string }> = {
  center: { title: 'Głowa prosto', hint: 'Patrz na środek ekranu i trzymaj głowę nieruchomo.' },
  left: { title: 'Odwróć głowę w lewo', hint: 'Około 15°. Wzrok zostaje na ekranie.' },
  right: { title: 'Odwróć głowę w prawo', hint: 'Około 15°. Wzrok zostaje na ekranie.' },
  top: { title: 'Unieś głowę lekko do góry', hint: 'Około 15°. Wzrok zostaje na ekranie.' },
  bottom: { title: 'Opuść głowę lekko w dół', hint: 'Około 15°. Wzrok zostaje na ekranie.' },
}

// ---- Schedule ----------------------------------------------------------------

interface Step {
  target: CalibrationTarget
  pose: HeadPose
  poseIdx: number
  poseCount: number
  pointIdx: number
  pointCount: number
}

function calibrationSteps(variant: CalibrationVariant): Step[] {
  const { points, poses } = CALIBRATION_VARIANT_SHAPES[variant]
  return poses.flatMap((pose, poseIdx) =>
    points.map((target, pointIdx) => ({
      target,
      pose,
      poseIdx,
      poseCount: poses.length,
      pointIdx,
      pointCount: points.length,
    })),
  )
}

function validationSteps(variant: CalibrationVariant): Step[] {
  const targets = CALIBRATION_VARIANT_SHAPES[variant].validationTargets
  return targets.map((target, pointIdx) => ({
    target,
    pose: 'center' as const,
    poseIdx: 0,
    poseCount: 1,
    pointIdx,
    pointCount: targets.length,
  }))
}

const SETTLE_MS = 600
const COLLECT_MS = 1100
const MAX_COLLECT_MS = 3000
const MIN_SAMPLES = 12
const TRAIL_MS = 1200

// Calibration, validation and live tracking all run on one dark-grey
// background so the luminance model feature stays constant (no extrapolation).
// The old three-brightness passes would triple a 5-pose calibration.
const TRACK_BG = '#393939'
const TRACK_LUM = 0.22

interface ValPointResult {
  target: CalibrationTarget
  errorPx: number
  errXPx: number
  errYPx: number
}

interface ValSummary {
  meanErrorPx: number
  meanErrXPx: number
  meanErrYPx: number
  diagonalPct: number
  points: ValPointResult[]
}

/** Ground-truth probe: cursor position vs detected gaze at the moment Space was pressed. */
interface Probe {
  mx: number
  my: number
  gx: number
  gy: number
  rawX: number
  rawY: number
}

/**
 * Full session log for offline analysis: raw calibration samples (base feature
 * vectors + targets) allow refitting alternative models against real data.
 * Auto-saved to localStorage (SESSIONS_KEY, last 8) and downloadable as JSON.
 */
interface GazeSessionLog {
  startedAt: string
  userAgent: string
  screen: { w: number; h: number; dpr: number }
  featureNames: string[]
  variant: CalibrationVariant
  engine: Engine
  modelVersion: string | null
  headBaselineDeg: HeadDeg | null
  skippedPoses: HeadPose[]
  trackingLuminance: number
  calibrationSamples: { base: number[]; x: number; y: number }[]
  fitStats: FitStats | null
  validation: {
    target: CalibrationTarget
    predX: number
    predY: number
    errorPx: number
  }[]
  probes: (Probe & { base: number[] | null })[]
}

const SESSIONS_KEY = 'facewise.gazeLab.sessions'

type Engine = 'cnn' | 'landmarks'

const ENGINE_LABEL: Record<Engine, string> = {
  cnn: 'CNN Miłosza (jak kontener)',
  landmarks: 'Stary: landmarki + ridge',
}

const CNN_FEATURE_NAMES = ['cnn_pitch', 'cnn_yaw', 'head_axis_x', 'head_axis_y', 'head_axis_z']

const FEATURE_NAMES = [
  'left_iris_hx',
  'left_iris_vy_corner',
  'right_iris_hx',
  'right_iris_vy_corner',
  'left_openness',
  'right_openness',
  'yaw',
  'pitch',
  'roll',
  'nose_x',
  'nose_y',
  'tz_over_100',
  'bg_luminance',
]

function freshSession(
  variant: CalibrationVariant,
  engine: Engine = 'cnn',
  modelVersion: string | null = null,
): GazeSessionLog {
  return {
    startedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    screen: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
    featureNames: engine === 'cnn' ? CNN_FEATURE_NAMES : FEATURE_NAMES,
    variant,
    engine,
    modelVersion,
    headBaselineDeg: null,
    skippedPoses: [],
    trackingLuminance: TRACK_LUM,
    calibrationSamples: [],
    fitStats: null,
    validation: [],
    probes: [],
  }
}

const fmtSigned = (v: number) => `${v >= 0 ? '+' : ''}${Math.round(v)}`

interface Cursor {
  idx: number
  /** pose = waiting for the head to reach the step's pose (guidance screen). */
  stage: 'pose' | 'settle' | 'collect'
  stageStart: number
  good: number
  // validation accumulators
  sumX: number
  sumY: number
  // pose hold
  holdStart: number | null
  holdSamples: HeadDeg[]
}

function freshCursor(t: number, stage: Cursor['stage'] = 'pose'): Cursor {
  return {
    idx: 0,
    stage,
    stageStart: t,
    good: 0,
    sumX: 0,
    sumY: 0,
    holdStart: null,
    holdSamples: [],
  }
}

export default function AdminEyeTrackingLab() {
  const theme = useTheme()
  const c = theme.affexy.colors

  // --- engine refs (never re-created across renders) ---
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const trackerRef = useRef<GazeTracker | null>(null)
  const modelRef = useRef<GazeModel>(new GazeModel())
  const cnnRef = useRef<CnnExtractor | null>(null)
  const cnnCalRef = useRef(new CnnCalibrator())
  const engineRef = useRef<Engine>('cnn')
  const filterRef = useRef(new OneEuroFilter2D(0.7, 0.12))

  // --- overlay DOM refs (mutated per-frame, bypassing React) ---
  const dotRef = useRef<HTMLDivElement>(null)
  const trailCanvasRef = useRef<HTMLCanvasElement>(null)
  const targetElRef = useRef<HTMLDivElement>(null)
  const trailPointsRef = useRef<{ x: number; y: number; t: number }[]>([])
  const trailEnabledRef = useRef(true)
  const mousePosRef = useRef<{ x: number; y: number } | null>(null)
  const lastGazeRef = useRef<{
    x: number
    y: number
    t: number
    rawX: number
    rawY: number
    base: number[]
  } | null>(null)
  const sessionRef = useRef<GazeSessionLog>(freshSession(DEFAULT_CALIBRATION_VARIANT))

  // --- state machine refs (read inside the stable frame handler) ---
  const phaseRef = useRef<Phase>('setup')
  const stepsRef = useRef<Step[]>([])
  const cursorRef = useRef<Cursor>(freshCursor(0))
  const baselineRef = useRef<HeadDeg | null>(null)
  const valResultsRef = useRef<ValPointResult[]>([])
  const lastHudT = useRef(0)
  const lastPoseViewT = useRef(0)

  // --- render state ---
  const [phase, setPhaseState] = useState<Phase>('setup')
  const [variant, setVariant] = useState<CalibrationVariant>(DEFAULT_CALIBRATION_VARIANT)
  const [cameraState, setCameraState] = useState<'starting' | 'ready' | 'error'>('starting')
  const [modelState, setModelState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [engine, setEngineState] = useState<Engine>('cnn')
  const [cnnState, setCnnState] = useState<{
    status: 'loading' | 'ready' | 'error'
    version?: string
    message?: string
  }>({ status: 'loading' })
  const [inferenceMs, setInferenceMs] = useState(0)
  const [hud, setHud] = useState<{ faceOk: boolean; blink: boolean; fps: number; head: HeadDeg | null }>({
    faceOk: false,
    blink: false,
    fps: 0,
    head: null,
  })
  const [stepIdx, setStepIdx] = useState(0)
  const [stage, setStage] = useState<Cursor['stage']>('pose')
  const [poseView, setPoseView] = useState<PoseView>(EMPTY_POSE_VIEW)
  const [fitStats, setFitStats] = useState<FitStats | null>(null)
  const [valSummary, setValSummary] = useState<ValSummary | null>(null)
  const [fitError, setFitError] = useState(false)
  const [trailEnabled, setTrailEnabled] = useState(true)
  const [propsEnabled, setPropsEnabled] = useState(true)
  const [probes, setProbes] = useState<Probe[]>([])

  const setPhase = useCallback((p: Phase) => {
    phaseRef.current = p
    setPhaseState(p)
  }, [])

  /**
   * The feature row the active engine calibrates and predicts on, or null when
   * this frame is unusable for it. CNN frames the extractor skipped while busy
   * come through with `cnn: null` and are simply not counted.
   */
  const usableFeatures = useCallback((frame: GazeFrame): number[] | null => {
    if (engineRef.current === 'cnn') return frame.cnn && !frame.cnnBlink ? frame.cnn : null
    return frame.faceOk && !frame.blink && frame.features ? [...frame.features.base, TRACK_LUM] : null
  }, [])

  const predictGaze = useCallback(
    (f: number[]) => (engineRef.current === 'cnn' ? cnnCalRef.current.predict(f) : modelRef.current.predict(f)),
    [],
  )

  const setEngine = useCallback((next: Engine) => {
    engineRef.current = next
    setEngineState(next)
    // The CNN costs a forward pass per frame; the landmark model runs at full rate without it.
    if (trackerRef.current) trackerRef.current.cnn = next === 'cnn' ? cnnRef.current : null
    setValSummary(null)
    setFitStats(null)
  }, [])

  const setCursorStage = useCallback((cur: Cursor, next: Cursor['stage'], t: number) => {
    cur.stage = next
    cur.stageStart = t
    cur.holdStart = null
    cur.holdSamples = []
    if (next === 'pose') {
      lastPoseViewT.current = 0
      setPoseView({ ...EMPTY_POSE_VIEW, establishing: baselineRef.current === null })
    }
    setStage(next)
  }, [])

  /** Upsert the current session into localStorage (keeps the last 8). */
  const persistSession = useCallback(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(SESSIONS_KEY) ?? '[]',
      ) as GazeSessionLog[]
      const others = stored.filter((s) => s.startedAt !== sessionRef.current.startedAt)
      others.push(sessionRef.current)
      localStorage.setItem(SESSIONS_KEY, JSON.stringify(others.slice(-8)))
    } catch {
      /* quota exceeded or corrupt store — logging must never break tracking */
    }
  }, [])

  const downloadSession = useCallback(() => {
    const blob = new Blob([JSON.stringify(sessionRef.current, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gaze-session-${sessionRef.current.startedAt.replace(/[:.]/g, '-')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  const drawTrail = useCallback((now: number) => {
    const canvas = trailCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const pts = trailPointsRef.current
    if (!trailEnabledRef.current || pts.length < 2) return
    for (let i = 1; i < pts.length; i++) {
      const age = (now - pts[i].t) / TRAIL_MS
      ctx.strokeStyle = `rgba(255, 122, 92, ${Math.max(0, 0.55 * (1 - age))})`
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(pts[i - 1].x, pts[i - 1].y)
      ctx.lineTo(pts[i].x, pts[i].y)
      ctx.stroke()
    }
  }, [])

  const startValidation = useCallback(
    (t: number) => {
      stepsRef.current = validationSteps(sessionRef.current.variant)
      valResultsRef.current = []
      cursorRef.current = freshCursor(t)
      setCursorStage(cursorRef.current, 'pose', t)
      setStepIdx(0)
      setPhase('validating')
    },
    [setCursorStage, setPhase],
  )

  const finishCalibration = useCallback(
    (t: number) => {
      let stats: FitStats | null
      if (engineRef.current === 'cnn') {
        const cal = cnnCalRef.current
        const rows = cal.samples()
        stats = cal.fit()
          ? {
              samples: rows.length,
              trimmed: 0,
              rmsResidual: Math.sqrt(
                rows.reduce((acc, r) => {
                  const p = cal.predict(r.features)!
                  return acc + (p.x - r.x) ** 2 + (p.y - r.y) ** 2
                }, 0) / rows.length,
              ),
            }
          : null
        sessionRef.current.calibrationSamples = rows.map((r) => ({ base: r.features, x: r.x, y: r.y }))
      } else {
        stats = modelRef.current.fit()
        sessionRef.current.calibrationSamples = modelRef.current.exportSamples()
      }
      sessionRef.current.fitStats = stats
      persistSession()
      if (!stats) {
        setFitError(true)
        exitFullscreenAndUnlock()
        setPhase('setup')
        return
      }
      setFitStats(stats)
      startValidation(t)
    },
    [persistSession, setPhase, startValidation],
  )

  const finishValidation = useCallback(() => {
    const points = valResultsRef.current
    const n = points.length || 1
    const meanErrorPx = points.length
      ? points.reduce((a, p) => a + p.errorPx, 0) / n
      : Number.NaN
    const diagonal = Math.hypot(window.innerWidth, window.innerHeight)
    setValSummary({
      meanErrorPx,
      meanErrXPx: points.reduce((a, p) => a + p.errXPx, 0) / n,
      meanErrYPx: points.reduce((a, p) => a + p.errYPx, 0) / n,
      diagonalPct: (meanErrorPx / diagonal) * 100,
      points,
    })
    persistSession()
    setPhase('summary')
  }, [persistSession, setPhase])

  /** Move to step `next` (or finish the phase). A new pose opens its guidance screen. */
  const advanceTo = useCallback(
    (next: number, t: number) => {
      const steps = stepsRef.current
      const cur = cursorRef.current
      if (next >= steps.length) {
        if (phaseRef.current === 'calibrating') finishCalibration(t)
        else finishValidation()
        return
      }
      const poseChanged = steps[next].poseIdx !== steps[cur.idx]?.poseIdx
      cur.idx = next
      cur.good = 0
      cur.sumX = 0
      cur.sumY = 0
      setCursorStage(cur, poseChanged ? 'pose' : 'settle', t)
      setStepIdx(next)
    },
    [finishCalibration, finishValidation, setCursorStage],
  )

  /** "Pomiń pozę": jump to the first step of the next pose. */
  const skipPose = useCallback(() => {
    const steps = stepsRef.current
    const cur = cursorRef.current
    const step = steps[cur.idx]
    if (!step) return
    if (baselineRef.current === null) {
      // Skipping the very first pose still needs a zero for the next ones.
      const h = hud.head
      baselineRef.current = h ?? { yaw: 0, pitch: 0 }
      sessionRef.current.headBaselineDeg = baselineRef.current
    }
    sessionRef.current.skippedPoses.push(step.pose)
    let next = cur.idx
    while (next < steps.length && steps[next].poseIdx === step.poseIdx) next++
    advanceTo(next, performance.now())
  }, [advanceTo, hud.head])

  /** Per-frame pose acquisition: hold inside the entry band for POSE_HOLD_MS. */
  const runPoseStage = useCallback(
    (cur: Cursor, step: Step, head: HeadDeg | null, t: number) => {
      const baseline = baselineRef.current
      let inside = false
      let off: HeadDeg | null = null
      if (head) {
        if (baseline === null) {
          // First pose: no zero yet. Accept any roughly frontal head that stays
          // still, and take its mean as the zero for every later pose.
          off = poseOffset(head, null, 'center')
          const frontal = Math.abs(head.yaw) <= BASELINE_MAX_YAW && Math.abs(head.pitch) <= BASELINE_MAX_PITCH
          const anchor = cur.holdSamples[0]
          inside =
            frontal &&
            (!anchor || within({ yaw: head.yaw - anchor.yaw, pitch: head.pitch - anchor.pitch }, POSE_ENTER_DEG))
        } else {
          off = poseOffset(head, baseline, step.pose)
          inside = within(off, POSE_ENTER_DEG)
        }
      }

      if (inside && head) {
        if (cur.holdStart === null) cur.holdStart = t
        cur.holdSamples.push(head)
      } else {
        cur.holdStart = null
        cur.holdSamples = []
      }
      const progress = cur.holdStart === null ? 0 : clamp01((t - cur.holdStart) / POSE_HOLD_MS)

      if (t - lastPoseViewT.current > 66) {
        lastPoseViewT.current = t
        setPoseView({ face: head !== null, off, inside, progress, establishing: baseline === null })
      }

      if (progress < 1) return
      if (baseline === null) {
        const n = cur.holdSamples.length
        baselineRef.current = {
          yaw: cur.holdSamples.reduce((a, h) => a + h.yaw, 0) / n,
          pitch: cur.holdSamples.reduce((a, h) => a + h.pitch, 0) / n,
        }
        sessionRef.current.headBaselineDeg = baselineRef.current
      }
      setCursorStage(cur, 'settle', t)
    },
    [setCursorStage],
  )

  /** Stable per-frame handler — everything it touches lives in refs. */
  const handleFrame = useCallback(
    (frame: GazeFrame) => {
      const t = frame.t
      const phaseNow = phaseRef.current
      const head = frame.faceOk && frame.features ? headFromBase(frame.features.base) : null

      // Throttled HUD refresh (~4 Hz) so React isn't re-rendering at 30–60 fps.
      if (t - lastHudT.current > 250) {
        lastHudT.current = t
        setHud({ faceOk: frame.faceOk, blink: frame.blink, fps: Math.round(frame.fps), head })
      }

      if (phaseNow === 'calibrating' || phaseNow === 'validating') {
        const steps = stepsRef.current
        const cur = cursorRef.current
        const step = steps[cur.idx]
        if (!step) return

        if (cur.stage === 'pose') {
          runPoseStage(cur, step, head, t)
          return
        }

        // Head drifted out of the pose mid-point: stop and re-acquire, then
        // redo this point from the settle stage (sandbox: collect_point).
        if (head && !within(poseOffset(head, baselineRef.current, step.pose), POSE_TOLERANCE_DEG)) {
          cur.good = 0
          cur.sumX = 0
          cur.sumY = 0
          setCursorStage(cur, 'pose', t)
          return
        }

        if (cur.stage === 'settle') {
          targetElRef.current?.style.setProperty('--p', '0')
          if (t - cur.stageStart >= SETTLE_MS) {
            cur.stage = 'collect'
            cur.stageStart = t
            cur.good = 0
            cur.sumX = 0
            cur.sumY = 0
          }
          return
        }

        // collect stage. A CNN result lands one forward pass after its camera
        // frame; one captured before this point settled must not train it.
        const target = step.target
        const f = t >= cur.stageStart ? usableFeatures(frame) : null
        if (f) {
          if (phaseNow === 'calibrating') {
            if (engineRef.current === 'cnn') cnnCalRef.current.add(f, target.x, target.y)
            else modelRef.current.addSample(f, target.x, target.y)
          } else {
            const p = predictGaze(f)
            if (p) {
              cur.sumX += p.x
              cur.sumY += p.y
            }
          }
          cur.good++
        }
        const elapsed = t - cur.stageStart
        targetElRef.current?.style.setProperty(
          '--p',
          String(Math.min(elapsed / COLLECT_MS, 1)),
        )
        const done =
          (elapsed >= COLLECT_MS && cur.good >= MIN_SAMPLES) || elapsed >= MAX_COLLECT_MS
        if (!done) return

        if (phaseNow === 'validating' && cur.good > 0) {
          const px = (cur.sumX / cur.good) * window.innerWidth
          const py = (cur.sumY / cur.good) * window.innerHeight
          const tx = target.x * window.innerWidth
          const ty = target.y * window.innerHeight
          valResultsRef.current.push({
            target,
            errorPx: Math.hypot(px - tx, py - ty),
            errXPx: Math.abs(px - tx),
            errYPx: Math.abs(py - ty),
          })
          sessionRef.current.validation.push({
            target,
            predX: px,
            predY: py,
            errorPx: Math.hypot(px - tx, py - ty),
          })
        }

        advanceTo(cur.idx + 1, t)
        return
      }

      if (phaseNow === 'tracking') {
        const dot = dotRef.current
        if (!frame.faceOk || !frame.features) {
          if (dot) dot.style.opacity = '0'
          return
        }
        const f = usableFeatures(frame)
        if (!f) return // blink or a frame the CNN skipped — freeze the dot
        const p = predictGaze(f)
        if (!p) return
        const sm = filterRef.current.filter(clamp01(p.x), clamp01(p.y), t / 1000)
        const x = sm.x * window.innerWidth
        const y = sm.y * window.innerHeight
        lastGazeRef.current = {
          x,
          y,
          t,
          rawX: p.x * window.innerWidth,
          rawY: p.y * window.innerHeight,
          base: f,
        }
        if (dot) {
          dot.style.opacity = '1'
          dot.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
        }
        const pts = trailPointsRef.current
        pts.push({ x, y, t })
        while (pts.length && t - pts[0].t > TRAIL_MS) pts.shift()
        drawTrail(t)
      }
    },
    [advanceTo, drawTrail, predictGaze, runPoseStage, setCursorStage, usableFeatures],
  )

  // Camera + landmarker lifecycle (StrictMode-safe: full teardown in cleanup).
  useEffect(() => {
    let cancelled = false
    const video = videoRef.current
    if (!video) return
    const tracker = new GazeTracker()
    trackerRef.current = tracker

    ;(async () => {
      // Load the landmarker while the user answers the camera prompt, and
      // reflect its status immediately — the camera prompt can sit unanswered
      // for a long time.
      const modelOkPromise = tracker
        .init(video)
        .then(() => true)
        .catch(() => false)
      void modelOkPromise.then((ok) => {
        if (!cancelled) setModelState(ok ? 'ready' : 'error')
      })
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop())
          return
        }
        streamRef.current = stream
        video.srcObject = stream
        await video.play().catch(() => {})
        setCameraState('ready')
      } catch {
        if (!cancelled) setCameraState('error')
      }
      const modelOk = await modelOkPromise
      if (cancelled) return
      if (modelOk) {
        tracker.onFrame = handleFrame
        tracker.cnn = engineRef.current === 'cnn' ? cnnRef.current : null
        tracker.start()
      }
    })()

    return () => {
      cancelled = true
      tracker.dispose()
      streamRef.current?.getTracks().forEach((tr) => tr.stop())
      streamRef.current = null
      if (video.srcObject) video.srcObject = null
      exitFullscreenAndUnlock()
    }
  }, [handleFrame])

  // Miłosz's network: fetched once per browser (Cache Storage), independent of
  // the camera effect so a remount never downloads 45 MB twice.
  useEffect(() => {
    let cancelled = false
    const extractor = new CnnExtractor()
    ;(async () => {
      try {
        const { bytes, version } = await loadLabModel()
        if (cancelled) return
        await extractor.init(bytes)
        if (cancelled) return
        cnnRef.current = extractor
        if (trackerRef.current && engineRef.current === 'cnn') trackerRef.current.cnn = extractor
        setCnnState({ status: 'ready', version })
      } catch (err) {
        if (!cancelled) setCnnState({ status: 'error', message: err instanceof Error ? err.message : String(err) })
      }
    })()
    const timer = window.setInterval(() => setInferenceMs(extractor.status().inferenceMs), 1000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      if (trackerRef.current?.cnn === extractor) trackerRef.current.cnn = null
      cnnRef.current = null
      extractor.dispose()
    }
  }, [])

  // Size the trail canvas to the viewport.
  useEffect(() => {
    const resize = () => {
      const canvas = trailCanvasRef.current
      if (canvas) {
        canvas.width = window.innerWidth
        canvas.height = window.innerHeight
      }
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [phase])

  const beginCalibration = useCallback(() => {
    setFitError(false)
    setValSummary(null)
    setFitStats(null)
    setProbes([])
    sessionRef.current = freshSession(variant, engineRef.current, engineRef.current === 'cnn' ? cnnState.version ?? null : null)
    modelRef.current.clear()
    cnnCalRef.current.clear()
    baselineRef.current = null
    stepsRef.current = calibrationSteps(variant)
    void enterFullscreen()
    const t = performance.now()
    cursorRef.current = freshCursor(t)
    setCursorStage(cursorRef.current, 'pose', t)
    setStepIdx(0)
    setPhase('calibrating')
  }, [cnnState.version, setCursorStage, setPhase, variant])

  const startTracking = useCallback(() => {
    filterRef.current.reset()
    trailPointsRef.current = []
    lastGazeRef.current = null
    setProbes([])
    // Drop focus from the clicked button so Space records a probe instead of
    // re-activating it.
    ;(document.activeElement as HTMLElement | null)?.blur?.()
    void enterFullscreen()
    setPhase('tracking')
  }, [setPhase])

  const exitToSetup = useCallback(() => {
    exitFullscreenAndUnlock()
    setPhase('setup')
  }, [setPhase])

  // Escape aborts any fullscreen phase (the first Esc press may only exit
  // browser fullscreen; a second one lands here).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phaseRef.current !== 'setup') exitToSetup()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [exitToSetup])

  // Ground-truth probes: while tracking, Space stamps cursor position (where
  // the user is actually looking) vs the detected gaze at that instant.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || phaseRef.current !== 'tracking') return
      e.preventDefault() // keep Space from re-triggering a focused button
      const m = mousePosRef.current
      const g = lastGazeRef.current
      if (m && g) {
        const probe = { mx: m.x, my: m.y, gx: g.x, gy: g.y, rawX: g.rawX, rawY: g.rawY }
        setProbes((prev) => [...prev.slice(-9), probe])
        sessionRef.current.probes.push({ ...probe, base: g.base })
        persistSession()
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('keydown', onKey)
    }
  }, [persistSession])

  const ready =
    cameraState === 'ready' && modelState === 'ready' && (engine !== 'cnn' || cnnState.status === 'ready')
  const fitted = engine === 'cnn' ? cnnCalRef.current.isFitted : modelRef.current.isFitted
  const steps = stepsRef.current
  const step: Step | undefined = steps[Math.min(stepIdx, steps.length - 1)]
  const overlayVisible = phase !== 'setup'
  const shape = CALIBRATION_VARIANT_SHAPES[variant]
  const headRel =
    hud.head && baselineRef.current
      ? poseOffset(hud.head, baselineRef.current, 'center')
      : null

  const statusChip = (label: string, ok: boolean, pending = false) => (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: pending ? c.ink08 : ok ? 'rgba(46,164,79,0.12)' : 'rgba(207,34,46,0.10)',
        color: pending ? c.ink56 : ok ? c.success : theme.palette.error.main,
        fontWeight: 600,
      }}
    />
  )

  return (
    <ResearcherShell crumbs={['Admin', 'Laboratorium eye trackingu']} hideTopBarAction>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ color: c.ink }}>
            Laboratorium eye trackingu
          </Typography>
          <Typography sx={{ fontSize: 14, color: c.ink56, maxWidth: 720 }}>
            Piaskownica wzroku na żywo. Domyślnie liczy silnik Miłosza — ten sam plik modelu
            (v0.1.0), co kontener gaze na AWS, i ta sama regresja po kalibracji — tylko
            w przeglądarce zamiast na serwerze. Tylko dla admina; nic tutaj nie dotyka badań ani
            respondentów.
          </Typography>
        </Box>

        <Paper sx={{ p: 3, borderRadius: '16px', border: `1px solid ${c.ink08}` }}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
              alignItems: 'start',
            }}
          >
            <Box>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  bgcolor: '#000',
                  aspectRatio: '4 / 3',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {/* The video element stays mounted for the whole session — the
                    tracker reads frames from it even while the overlay is up. */}
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)', // selfie mirror, display only
                  }}
                />
                {cameraState === 'starting' && (
                  <CircularProgress size={28} sx={{ position: 'absolute', color: '#fff' }} />
                )}
                {cameraState === 'error' && (
                  <Typography
                    sx={{ position: 'absolute', color: '#fff', fontSize: 13, px: 2, textAlign: 'center' }}
                  >
                    Kamera niedostępna — sprawdź uprawnienia przeglądarki i odśwież.
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                {statusChip(
                  cameraState === 'ready' ? 'Kamera działa' : 'Kamera',
                  cameraState === 'ready',
                  cameraState === 'starting',
                )}
                {statusChip(
                  modelState === 'ready'
                    ? 'Model wczytany'
                    : modelState === 'error'
                      ? 'Model nie wstał'
                      : 'Wczytuję model…',
                  modelState === 'ready',
                  modelState === 'loading',
                )}
                {engine === 'cnn' &&
                  statusChip(
                    cnnState.status === 'ready'
                      ? `CNN ${cnnState.version} wczytany`
                      : cnnState.status === 'error'
                        ? 'CNN nie wstał'
                        : 'Pobieram CNN (45 MB)…',
                    cnnState.status === 'ready',
                    cnnState.status === 'loading',
                  )}
                {statusChip(hud.faceOk ? 'Twarz wykryta' : 'Brak twarzy', hud.faceOk, !ready)}
                <Chip size="small" label={`${hud.fps} fps`} sx={{ bgcolor: c.ink08, color: c.ink56 }} />
                {engine === 'cnn' && inferenceMs > 0 && (
                  <Chip
                    size="small"
                    label={`CNN ${Math.round(inferenceMs)} ms/klatkę`}
                    sx={{ bgcolor: c.ink08, color: c.ink56 }}
                  />
                )}
                {hud.head && (
                  <Chip
                    size="small"
                    label={`yaw ${fmtSigned(hud.head.yaw)}° · pitch ${fmtSigned(hud.head.pitch)}°`}
                    sx={{ bgcolor: c.ink08, color: c.ink56, fontFamily: 'ui-monospace, monospace' }}
                  />
                )}
              </Stack>
              {engine === 'cnn' && cnnState.status === 'error' && (
                <Typography sx={{ mt: 1, fontSize: 12, color: theme.palette.error.main }}>
                  {cnnState.message}
                </Typography>
              )}
            </Box>

            <Stack spacing={2}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: c.ink }}>Silnik</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={engine}
                onChange={(_, v: Engine | null) => v && setEngine(v)}
                sx={{ flexWrap: 'wrap' }}
              >
                {(Object.keys(ENGINE_LABEL) as Engine[]).map((e) => (
                  <ToggleButton key={e} value={e} sx={{ textTransform: 'none', px: 1.5 }}>
                    {ENGINE_LABEL[e]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: c.ink }}>
                Wariant kalibracji
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={variant}
                onChange={(_, v: CalibrationVariant | null) => v && setVariant(v)}
                sx={{ flexWrap: 'wrap' }}
              >
                {CALIBRATION_VARIANTS.map((v) => (
                  <ToggleButton key={v} value={v} sx={{ textTransform: 'none', px: 1.5 }}>
                    {VARIANT_LABEL[v]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 14, color: c.ink56 }}>
                1. <b>Kalibracja</b> — przed każdą pozą ({shape.poses.map((p) => POSE_COPY[p].title.toLowerCase()).join(', ')})
                ekran mówi, jak ustawić głowę, i czeka, aż kąt się zgadza (±{POSE_ENTER_DEG}°). Potem
                patrzysz na {shape.points.length} kropek, ruszając tylko oczami. Jeśli głowa zjedzie
                z pozy o więcej niż {POSE_TOLERANCE_DEG}°, punkt się zatrzymuje i wraca instrukcja.
                <br />
                2. <b>Walidacja</b> — {shape.validationTargets.length} nowych punktów, głowa prosto;
                wynik to średni błąd w pikselach.
                <br />
                3. <b>Na żywo</b> — pusty ekran i kropka tam, gdzie wykrywa wzrok. Spacja, gdy patrzysz
                na kursor, zapisuje pomiar „prawda vs wykrycie".
              </Typography>
              {fitError && (
                <Typography sx={{ fontSize: 13, color: theme.palette.error.main }}>
                  Kalibracja nie wyszła — za mało czystych próbek (zgubiona twarz albo za dużo
                  mrugnięć). Spróbuj jeszcze raz z twarzą na środku i lepszym światłem.
                </Typography>
              )}
              {valSummary && (
                <Typography sx={{ fontSize: 13, color: c.ink56 }}>
                  Ostatnia kalibracja: średni błąd walidacji{' '}
                  <b>{Math.round(valSummary.meanErrorPx)} px</b> (
                  {valSummary.diagonalPct.toFixed(1)}% przekątnej ekranu).
                </Typography>
              )}
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  disabled={!ready || !hud.faceOk}
                  onClick={beginCalibration}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {fitted ? 'Kalibruj od nowa' : 'Zacznij kalibrację'}
                </Button>
                {fitted && (
                  <Button
                    variant="outlined"
                    disabled={!ready}
                    onClick={startTracking}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Wzrok na żywo
                  </Button>
                )}
              </Stack>
              <Typography sx={{ fontSize: 12, color: c.ink48 }}>
                Wszystko liczy się w tej karcie — obraz z kamery ani dane wzroku nie wychodzą
                z komputera.
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Stack>

      {/* ---- Fullscreen overlay: calibration / validation / summary / tracking ---- */}
      {overlayVisible && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            bgcolor: TRACK_BG,
            cursor: phase === 'tracking' ? 'crosshair' : phase === 'summary' || stage === 'pose' ? 'default' : 'none',
            overflow: 'hidden',
          }}
        >
          {(phase === 'calibrating' || phase === 'validating') && step && stage === 'pose' && (
            <PoseGuide
              view={poseView}
              title={phase === 'validating' ? 'Walidacja — głowa prosto' : POSE_COPY[step.pose].title}
              hint={POSE_COPY[step.pose].hint}
              counter={
                phase === 'calibrating' && step.poseCount > 1
                  ? `Ustawienie głowy ${step.poseIdx + 1} / ${step.poseCount}`
                  : null
              }
              accent={c.coral}
              onSkip={skipPose}
              onCancel={exitToSetup}
            />
          )}

          {(phase === 'calibrating' || phase === 'validating') && step && stage !== 'pose' && (
            <>
              <Button
                size="small"
                onClick={exitToSetup}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  textTransform: 'none',
                  color: 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                }}
              >
                Przerwij (Esc)
              </Button>
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 14,
                }}
              >
                {phase === 'calibrating'
                  ? `Kalibracja — patrz na kropkę · ${POSE_COPY[step.pose].title.toLowerCase()}${step.poseCount > 1 ? ` (${step.poseIdx + 1}/${step.poseCount})` : ''} · punkt ${step.pointIdx + 1} / ${step.pointCount}`
                  : `Walidacja — patrz na kropkę · ${step.pointIdx + 1} / ${step.pointCount}`}
                {!hud.faceOk && '  ·  zgubiona twarz!'}
              </Typography>
              <Box
                ref={targetElRef}
                style={{ '--p': 0 } as CSSProperties}
                sx={{
                  position: 'absolute',
                  left: `${step.target.x * 100}%`,
                  top: `${step.target.y * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 46,
                  height: 46,
                  display: 'grid',
                  placeItems: 'center',
                  transition: 'left 0.45s ease, top 0.45s ease',
                }}
              >
                {/* progress ring driven per-frame via the --p custom property */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    background: `conic-gradient(${c.coral} calc(var(--p) * 360deg), rgba(255,255,255,0.14) 0deg)`,
                    mask: 'radial-gradient(circle, transparent 58%, black 60%)',
                  }}
                />
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: '#ffffff',
                    boxShadow: `0 0 12px 2px ${c.coral}`,
                  }}
                />
              </Box>
            </>
          )}

          {phase === 'summary' && valSummary && (
            <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: '20px',
                  maxWidth: 480,
                  bgcolor: '#141a2a',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 1 }}>
                  Kalibracja gotowa
                </Typography>
                <Typography sx={{ fontSize: 40, fontWeight: 800, color: c.coral, lineHeight: 1 }}>
                  {Number.isFinite(valSummary.meanErrorPx)
                    ? `${Math.round(valSummary.meanErrorPx)} px`
                    : '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                  średni błąd walidacji · {valSummary.diagonalPct.toFixed(1)}% przekątnej
                  {' · '}x {Math.round(valSummary.meanErrXPx)} px / y{' '}
                  {Math.round(valSummary.meanErrYPx)} px
                  {fitStats &&
                    ` · ${fitStats.samples} próbek (${fitStats.trimmed} odrzuconych)`}
                  {sessionRef.current.skippedPoses.length > 0 &&
                    ` · pominięte pozy: ${sessionRef.current.skippedPoses.join(', ')}`}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
                  {valSummary.points.map((p, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`P${i + 1}: ${Math.round(p.errorPx)}px`}
                      sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={startTracking}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: c.coral }}
                  >
                    Wzrok na żywo
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={beginCalibration}
                    sx={{ textTransform: 'none', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
                  >
                    Kalibruj od nowa
                  </Button>
                  <Button
                    onClick={downloadSession}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}
                  >
                    Eksport danych
                  </Button>
                  <Button
                    onClick={exitToSetup}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}
                  >
                    Wyjdź
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}

          {phase === 'tracking' && (
            <>
              <canvas
                ref={trailCanvasRef}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
              {propsEnabled && <GazePropsLayer gazeRef={lastGazeRef} />}
              {/* Ground-truth probes: green ring = cursor (truth), coral dot =
                  detected gaze, dashed line = the error vector. */}
              <svg
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                {probes.slice(-5).map((p, i, arr) => (
                  <g key={`${p.mx}-${p.my}-${i}`} opacity={0.4 + (0.6 * (i + 1)) / arr.length}>
                    <line
                      x1={p.mx}
                      y1={p.my}
                      x2={p.gx}
                      y2={p.gy}
                      stroke="rgba(255,255,255,0.55)"
                      strokeDasharray="5 5"
                    />
                    <circle cx={p.mx} cy={p.my} r={11} fill="none" stroke="#4ade80" strokeWidth={2} />
                    <circle cx={p.mx} cy={p.my} r={2.5} fill="#4ade80" />
                    <circle cx={p.gx} cy={p.gy} r={5} fill="rgba(255,122,92,0.95)" />
                  </g>
                ))}
              </svg>
              {probes.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    bgcolor: 'rgba(0,0,0,0.55)',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 12,
                    lineHeight: 1.7,
                    color: 'rgba(255,255,255,0.85)',
                    cursor: 'default',
                  }}
                >
                  <Box sx={{ fontWeight: 700, color: '#fff' }}>
                    bias x {fmtSigned(probes.reduce((a, p) => a + (p.gx - p.mx), 0) / probes.length)}
                    {' · '}y {fmtSigned(probes.reduce((a, p) => a + (p.gy - p.my), 0) / probes.length)}
                    {' px · '}
                    {probes.length} pomiarów
                  </Box>
                  {probes.slice(-5).map((p, i) => (
                    <Box key={i}>
                      Δx {fmtSigned(p.gx - p.mx)} · Δy {fmtSigned(p.gy - p.my)} · raw Δ(
                      {fmtSigned(p.rawX - p.mx)}, {fmtSigned(p.rawY - p.my)})
                    </Box>
                  ))}
                </Box>
              )}
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: 13,
                }}
              >
                Patrz na rekwizyty, żeby je „zasilić" · patrz na kursor i wciśnij Spację, żeby zapisać
                pomiar (zielony pierścień = prawda, koralowa kropka = wykrycie)
              </Typography>
              <Box
                ref={dotRef}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: c.coral,
                  border: '2px solid rgba(255,255,255,0.9)',
                  boxShadow: `0 0 18px 4px rgba(255,122,92,0.55)`,
                  opacity: 0,
                  pointerEvents: 'none',
                  willChange: 'transform',
                }}
              />
              {/* HUD */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ position: 'absolute', top: 16, right: 16, alignItems: 'center', cursor: 'default' }}
              >
                <Chip
                  size="small"
                  label={hud.faceOk ? (hud.blink ? 'mrugnięcie' : 'twarz ok') : 'brak twarzy'}
                  sx={{
                    bgcolor: hud.faceOk ? 'rgba(46,164,79,0.25)' : 'rgba(207,34,46,0.3)',
                    color: '#fff',
                  }}
                />
                {headRel && (
                  <Chip
                    size="small"
                    label={`głowa ${fmtSigned(headRel.yaw)}° / ${fmtSigned(headRel.pitch)}°`}
                    sx={{
                      bgcolor: within(headRel, 20) ? 'rgba(255,255,255,0.1)' : 'rgba(207,34,46,0.3)',
                      color: 'rgba(255,255,255,0.7)',
                      fontFamily: 'ui-monospace, monospace',
                    }}
                  />
                )}
                <Chip
                  size="small"
                  label={`${hud.fps} fps`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
                />
                <Button
                  size="small"
                  onClick={() => {
                    trailEnabledRef.current = !trailEnabledRef.current
                    setTrailEnabled(trailEnabledRef.current)
                  }}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.7)' }}
                >
                  {trailEnabled ? 'Ukryj ślad' : 'Pokaż ślad'}
                </Button>
                <Button
                  size="small"
                  onClick={() => setPropsEnabled((v) => !v)}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.7)' }}
                >
                  {propsEnabled ? 'Ukryj rekwizyty' : 'Pokaż rekwizyty'}
                </Button>
                <Button
                  size="small"
                  onClick={downloadSession}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.7)' }}
                >
                  Eksport danych
                </Button>
                <Button
                  size="small"
                  onClick={beginCalibration}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.7)' }}
                >
                  Kalibruj od nowa
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={exitToSetup}
                  sx={{
                    textTransform: 'none',
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.35)',
                  }}
                >
                  Wyjdź
                </Button>
              </Stack>
            </>
          )}
        </Box>
      )}
    </ResearcherShell>
  )
}
